import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTemplateStore, useAuthStore } from "@/store";
import { toast } from "sonner";
import {
  Play,
  Plus,
  Search,
  Edit,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Clock,
  Layers,
  MoreVertical,
  Filter,
  LogOut,
  ArrowRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TEMPLATE_CATEGORY_LABELS = {
  all: "Category: All",
  social: "Transform Image to video with AI",
  product: "Simple & Visual video templates",
  edited: "Simple edited video templates",
  marketing: "Fun & Creative video templates",
};

const CATEGORY_ORDER = ["social", "product", "edited", "marketing"];

const FORMAT_FILTER_LABELS = {
  all: "Format: All",
  instagram: "Format: Instagram (4:5)",
  portrait: "Portrait (9:16)",
  square: "Square (1:1)",
};

const FORMAT_ORDER = ["all", "instagram", "portrait", "square"];

const AdminNavbar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/assets/logo-creavideos.png" 
                alt="CreaVideos" 
                className="h-8 w-auto"
              />
            </Link>
            <span className="px-2 py-1 rounded bg-teal-600 text-xs font-semibold">ADMIN</span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-white/80 hover:text-white transition-colors font-medium">
              Dashboard
            </Link>
            <Link to="/admin/templates" className="text-white font-medium">
              Templates
            </Link>
            <Link to="/admin/users" className="text-white/80 hover:text-white transition-colors font-medium">
              Users
            </Link>
            <Link to="/admin/user-stats" className="text-white/80 hover:text-white transition-colors font-medium">
              User Stats
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-full hover:bg-white/10">
                <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                  <span className="text-sm font-medium">{user?.name?.[0]}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  User Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default function AdminTemplates() {
  const navigate = useNavigate();
  const { templates, fetchTemplates, deleteTemplate, updateTemplate, duplicateTemplate, isLoading } = useTemplateStore();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [format, setFormat] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    fetchTemplates(false); // Fetch all templates including private
  }, [fetchTemplates]);

  const categories = ["all", ...CATEGORY_ORDER].map((value) => ({
    value,
    label: TEMPLATE_CATEGORY_LABELS[value],
  }));

  const formatFilters = FORMAT_ORDER.map((value) => ({
    value,
    label: FORMAT_FILTER_LABELS[value],
  }));

  const filteredTemplates = useMemo(() => templates.filter((template) => {
    const normalizedSearch = search.toLowerCase();
    const matchesSearch = template.name.toLowerCase().includes(normalizedSearch) ||
      template.description?.toLowerCase().includes(normalizedSearch);
    const matchesCategory = category === "all" || template.category === category;

    const { canvas_width, canvas_height } = template;
    let matchesFormat = format === "all";
    if (format === "portrait" && canvas_width * 16 === canvas_height * 9) matchesFormat = true;
    if (format === "square" && canvas_width === canvas_height) matchesFormat = true;
    if (format === "instagram" && canvas_width * 5 === canvas_height * 4) matchesFormat = true;

    return matchesSearch && matchesCategory && matchesFormat;
  }), [templates, search, category, format]);

  const handleTogglePublic = async (template) => {
    const result = await updateTemplate(template.id, { is_public: !template.is_public });
    if (result) {
      toast.success(`Template ${result.is_public ? "published" : "unpublished"}`);
    }
  };

  const handleDelete = async () => {
    if (templateToDelete) {
      const success = await deleteTemplate(templateToDelete.id);
      if (success) {
        toast.success("Template deleted");
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    const duplicatedTemplate = await duplicateTemplate(template.id);
    if (!duplicatedTemplate) {
      toast.error("Failed to duplicate template");
      return;
    }

    toast.success(`Template duplicated as "${duplicatedTemplate.name}"`);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNavbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-1">Templates</h1>
              <p className="text-slate-600">Create and manage video templates</p>
            </div>
            <Link to="/admin/templates/new">
              <Button className="btn-primary rounded-full" data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </Link>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative md:w-64 lg:w-72 md:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white"
                data-testid="template-search"
              />
            </div>

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full md:w-80 lg:w-96 h-12 bg-white text-left [&>span]:flex-1 [&>span]:text-left" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="w-full md:w-64 h-12 bg-white text-left [&>span]:flex-1 [&>span]:text-left" data-testid="format-filter">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>
                {formatFilters.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Templates Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-64 animate-pulse" />
              ))}
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Layers className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No templates found</h3>
              <p className="text-slate-600 mb-4">Try adjusting your search or filters</p>
              <Link to="/admin/templates/new">
                <Button className="btn-primary rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`template-card-${template.id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-violet-100 to-sky-100 relative group">
                    {template.thumbnail_url ? (
                      <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-12 h-12 text-violet-400" />
                      </div>
                    )}
                    
                    {/* Edit Overlay - Appears on Hover */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => navigate(`/admin/templates/${template.id}/editor`)}
                          className="btn-primary rounded-full"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Template
                        </Button>
                        <Button
                          onClick={() => handleDuplicateTemplate(template)}
                          variant="secondary"
                          className="rounded-full"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate Template
                        </Button>
                      </div>
                    </div>
                    
                    {/* Status badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.is_public
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {template.is_public ? "Public" : "Draft"}
                      </span>
                    </div>

                    {/* Actions menu */}
                    <div className="absolute top-3 right-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/templates/${template.id}/editor`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateTemplate(template)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTogglePublic(template)}>
                            {template.is_public ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Publish
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setTemplateToDelete(template);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{template.name}</h3>
                    {template.description && (
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{template.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{template.canvas_width}×{template.canvas_height}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {template.duration}s
                      </span>
                      <span className="flex items-center gap-1">
                        <Layers className="w-4 h-4" />
                        {template.layers?.length || 0} layers
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the template
              &quot;{templateToDelete?.name}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
